import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { SectionTag } from './SectionTag';
import { BONOS } from './salesData';

/**
 * LOS 5 BONOS. Carrusel horizontal: una tarjeta completa por vez, con una
 * porción visible de la siguiente para que se entienda que hay más. Así la
 * sección deja de alargar la página sin perder ningún bono.
 */
export function BonosSection() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.firstElementChild as HTMLElement | null;
    if (!card) return;
    const step = card.offsetWidth + 14;
    setActive(Math.min(BONOS.length - 1, Math.round(track.scrollLeft / step)));
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, [onScroll]);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[index] as HTMLElement | undefined;
    if (card) track.scrollTo({
      left: card.offsetLeft - track.offsetLeft,
      behavior: 'smooth'
    });
  };
  return <section id="bonos" className="scroll-mt-24 overflow-hidden bg-bone py-11 sm:py-16" aria-labelledby="bonos-title" data-id="element-48" data-mp="muueycaxqaft">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8" data-id="element-49" data-mp="1alrh4xcxhtdv">
        <div className="max-w-3xl" data-id="element-50" data-mp="1ejp8a5q9epjw">
          <SectionTag data-id="element-51" data-mp="1emtjhn256lk2">Los 5 bonos incluidos</SectionTag>
          <h2 id="bonos-title" className="mt-4 font-display text-[1.95rem] leading-[1.06] text-charcoal sm:text-[2.7rem]" data-id="element-52" data-mp="10siy9fpct9ir">
            No solo aprendés a hacerlas.{' '}
            <span className="text-[#F47A2A]" data-id="element-53" data-mp="1thhlusc8x3vg">
              También te ayudamos a empezar a vender.
            </span>
          </h2>
          <p className="mt-3 font-sans text-[0.92rem] leading-snug text-charcoal-mid sm:leading-relaxed" data-id="element-54" data-mp="1yb2ioh23rrt2">
            Con tu compra recibís gratis 5 libros pensados para ayudarte a organizarte, evitar errores, presentar mejor tus cookies y empezar a construir tu negocio.
          </p>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} className="mt-7" data-id="element-55" data-mp="1t7pkiwvxpktj">
        <ul ref={trackRef} className="no-scrollbar flex snap-x snap-mandatory gap-3.5 overflow-x-auto px-5 pb-2 pt-2 sm:px-8 lg:mx-auto lg:max-w-6xl" aria-label="Bonos incluidos" data-id="element-56" data-mp="1kely3z3b9xmr">
          {BONOS.map(bono => <li key={bono.id} className="w-[87%] shrink-0 snap-start sm:w-[58%] lg:w-[calc(33.333%-0.6rem)]" data-id="element-57" data-mp="15i3js4zwyxjf">
              <article className="grid h-full grid-cols-[5.5rem_1fr] items-center gap-4 rounded-4xl bg-ivory p-3.5 shadow-bakery-sm sm:grid-cols-[6.5rem_1fr] sm:p-4" data-id="element-58" data-mp="11ht7kalj3aw4">
                <div className="relative" data-id="element-59" data-mp="3h4xa3mjuz92d">
                  <img src={bono.image} alt={`Portada del ${bono.label}: ${bono.title}.`} loading="lazy" decoding="async" className="aspect-[3/4] w-full rounded-xl bg-cream object-contain shadow-bakery-sm" data-id="element-60" data-mp="3h3nceygx42w9" />
                  <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#F47A2A] px-2 py-0.5 font-sans text-[0.44rem] font-bold uppercase tracking-[0.1em] text-white shadow-bakery-sm" data-id="element-61" data-mp="2yeytuj5wq9nn">
                    Bono gratis
                  </span>
                </div>

                <div data-id="element-62" data-mp="2ape9bvu3v4c9">
                  <p className="font-sans text-[0.54rem] font-bold uppercase tracking-editorial text-caramel" data-id="element-63" data-mp="2aq6639zfv844">
                    {bono.label}
                  </p>
                  <h3 className="mt-1 font-display text-[1rem] leading-tight text-charcoal" data-id="element-64" data-mp="2fm4rqptduc0u">
                    {bono.title}
                  </h3>
                  <p className="mt-1.5 font-sans text-[0.79rem] leading-snug text-charcoal-mid" data-id="element-65" data-mp="h7p55jb7pvxg">
                    {bono.body}
                  </p>
                  <p className="mt-2 border-l-2 border-[#F47A2A] pl-2.5 font-sans text-[0.75rem] font-bold leading-snug text-charcoal" data-id="element-66" data-mp="q7c500oamtoe">
                    {bono.angle}
                  </p>
                </div>
              </article>
            </li>)}
        </ul>

        {/* Indicadores: dejan claro que hay 5 tarjetas. */}
        <div className="mt-3 flex items-center justify-center gap-2 lg:hidden" data-id="element-67" data-mp="1dx6dk61kxw2u">
          {BONOS.map((bono, index) => <button key={bono.id} type="button" onClick={() => goTo(index)} aria-label={`Ver ${bono.label}: ${bono.title}`} aria-current={index === active} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${index === active ? 'w-6 bg-[#F47A2A]' : 'w-2 bg-charcoal/20'}`} data-id="element-68" data-mp="18yd2ek9rmh5u" data-mpi={index} />)}
        </div>
      </motion.div>
    </section>;
}